import { Classes, Dialog, InputGroup, MenuItem, Popover, TextArea } from "@blueprintjs/core"
import { ItemRenderer, MultiSelect } from "@blueprintjs/select"
import { FileLink } from "modules/common/components/files/FileLink"
import { DatePicker } from "modules/common/components/form/DatePicker"
import {
  filterProjectItemPredicate,
  ProjectSelect,
  ProjectSelectedOption,
  renderProjectOption,
} from "modules/common/components/form/ProjectSelect"
import { RemoveConfirmation } from "modules/common/components/form/RemoveConfirmation"
import { StandardFormButtons } from "modules/common/components/form/StandardFormButtons"
import { PlanrButton } from "modules/common/components/planr/button/Button"
import { PlanRDndUploader } from "modules/common/components/planr/dndUploader/FileDndUploader"
import { GeneralIcon } from "modules/common/components/planr/icon/Generalcon"
import { DATE_FORMAT } from "modules/common/constants"
import { isNewlyCreated } from "modules/common/models/entity"
import {
  DefaultSelectedOption,
  filterItemPredicate,
  matchTargetWidthPopover,
  renderSingleOption,
  SimpleSelect,
} from "modules/common/services/form/select"
import { texts } from "modules/common/texts"
import { OrderDictionaryItemType, OrderDictionaryType } from "modules/orders-manage/models/order-dictionary"
import {
  MessageType,
  OrderQuestionSnapshotType,
  SaveQuestion,
} from "modules/orders-manage/models/order-question"
import { EmployerDictionaryType } from "modules/spending/employee/models/employee-dictionary"
import moment from "moment"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import {
  BoardColumnSnapshotType,
  COLUMN_COLORS,
  DEFAULT_TAG_COLOR,
  QuestionTagSnapshotType,
  SystemColumns,
} from "../models/board"
import styles from "./QuestionDialog.module.scss"
import { QuestionComments } from "./QuestionComments"

const PeopleSelect = MultiSelect.ofType<SelectItem>()
const TagMultiSelect = MultiSelect.ofType<SelectItem>()

/** Выпадающий список ровно по ширине поля */
const MATCH_TARGET_WIDTH = matchTargetWidthPopover()

/**
 * Пункт списка с отметкой выбранного. Общий рендерер знает только про modifiers.active —
 * это подсветка пункта под клавиатурой, из-за неё первый пункт выглядел выбранным
 */
const renderCheckedOption = (
  selected: string[],
  dot?: (id: string) => string
): ItemRenderer<SelectItem> => (item, { handleClick, modifiers }) => {
  if (!modifiers.matchesPredicate) {
    return null
  }

  const checked = selected.includes(item.id)

  return (
    <MenuItem
      key={item.id}
      // подсветку пункта под клавиатурой делаем серой: синий фон читался как «выбрано»
      className={modifiers.active ? styles.optionActive : ""}
      disabled={modifiers.disabled}
      icon={checked ? "tick" : "blank"}
      onClick={handleClick}
      shouldDismissPopover={false}
      text={
        dot ? (
          <span className={styles.optionWithDot}>
            <span className={styles.tagDot} style={{ backgroundColor: dot(item.id) }} />
            <span style={{ color: dot(item.id) }}>{item.label}</span>
          </span>
        ) : (
          item.label
        )
      }
    />
  )
}

/** Окно задания: заголовок, описание, ответ, комментарии и боковая панель свойств */
export const QuestionDialog: React.FC<QuestionDialogProps> = (props) => {
  const {
    value,
    question,
    columns,
    tags,
    employee,
    orders,
    baseUrl,
    hideProject,
    currentUserId,
    canAddQuestion,
    onClose,
    onSubmit,
    onRemove,
    onAddMessage,
    onDeleteMessage,
    onCreateChild,
    onOpenQuestion,
    onSetReadiness,
    onShare,
    portfolioMembers,
    upload,
  } = props

  const [model, setModel] = useState<SaveQuestion>(value)
  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)
  // подсвечиваем пустое задание только после попытки сохранить, а не сразу при открытии
  const [emptyError, setEmptyError] = useState(false)

  useEffect(() => {
    setModel(value)
    setDirty(false)
    setEmptyError(false)
  }, [value])

  const isNew = isNewlyCreated(model.id)
  const canEdit = isNew ? !!canAddQuestion : question.canEditQuestion
  const canAnswer = isNew ? !!canAddQuestion : question.canAddAnswer
  const canAssign = isNew ? !!canAddQuestion : question.canAddImplementer || question.canEditQuestion

  const patch = useCallback((changes: Partial<SaveQuestion>) => {
    setModel((prev) => ({ ...prev, ...changes }))
    setDirty(true)

    if ("title" in changes || "description" in changes) {
      setEmptyError(false)
    }
  }, [])

  const close = useCallback(() => {
    if (dirty && !window.confirm("У вас есть несохранённые изменения. Закрыть окно?")) {
      return
    }

    onClose()
  }, [dirty, onClose])

  const submit = useCallback(async () => {
    // задание без заголовка и без описания сохранять нельзя — хватает одного из двух,
    // быстрое добавление на доске, например, создаёт карточку только с заголовком
    if (!model.title?.trim() && !model.description?.trim()) {
      setEmptyError(true)
      return
    }

    setSaving(true)
    const success = await onSubmit(model)
    setSaving(false)

    if (success) {
      setDirty(false)
    }
  }, [model, onSubmit])

  // «Просрочено» заполняется по сроку, вручную в неё не ставят
  const movable = useMemo(
    () =>
      columns.filter(
        (c) => c.key !== SystemColumns.expired && (c.key !== SystemColumns.done || question.canCompleteQuestion || isNew)
      ),
    [columns, question.canCompleteQuestion, isNew]
  )

  const currentColumn = columns.find((c) => c.key === (model.columnId || SystemColumns.inWork))

  // подсказка про перенос имеет смысл только на доске с системными колонками
  const reviewColumn = columns.find((c) => c.key === SystemColumns.review)


  const columnItems = useMemo(() => movable.map((c) => ({ id: c.key, label: c.name })), [movable])

  const columnItem = columnItems.find((c) => c.id === model.columnId) || null

  // у заданий без срока с сервера может прийти пустая дата (01.01.0001) — её не показываем
  const stopDate = useMemo(() => {
    const parsed = model.stopDate ? moment(model.stopDate, DATE_FORMAT) : null

    return parsed && parsed.isValid() && parsed.year() > 1900 ? parsed : null
  }, [model.stopDate])

  // в архивной колонке срок не подсвечиваем: задание туда уже сложили
  const expired =
    !model.completed && !currentColumn?.isArchive && !!stopDate && stopDate.isBefore(moment().startOf("day"))

  const project = orders && model.orderId ? orders.orders.find((o) => o.id === model.orderId) || null : null

  // задание открыл тот, кто его выполняет: ответ — то, ради чего он сюда пришёл.
  // тому, кто задание сам и выдал, подсвечивать нечего
  const waitsAnswer = useMemo(
    () =>
      !isNew &&
      canAnswer &&
      question.author?.id !== currentUserId &&
      (question.employers.some((e) => e.id === currentUserId) ||
        question.implementers.some((e) => e.id === currentUserId)),
    [isNew, canAnswer, question.author, question.employers, question.implementers, currentUserId]
  )

  const showReviewHint =
    !!reviewColumn &&
    waitsAnswer &&
    model.columnId !== SystemColumns.review &&
    model.columnId !== SystemColumns.done

  // работу закрывают исполнители, а если их не назначили — ответственные
  const executors = useMemo(
    () => (question.implementers.length > 0 ? question.implementers : question.employers),
    [question.implementers, question.employers]
  )

  const readiness = question.readiness
  const readyCount = readiness.filter((r) => executors.some((e) => e.id === r.employer?.id)).length
  const iAmExecutor = executors.some((e) => e.id === currentUserId)
  const myReadiness = readiness.some((r) => r.employer?.id === currentUserId)

  // на личной доске задания раздают только между её участниками
  const people = useMemo(() => {
    if (!portfolioMembers) {
      return employee.employee
    }

    return portfolioMembers.map((member) => ({ id: member.id, label: member.name }))
  }, [portfolioMembers, employee.employee])

  // моя отметка — последняя: после неё задание уйдёт на проверку само
  const iAmLast = iAmExecutor && !myReadiness && readyCount === executors.length - 1

  // задание самому себе: отмечаться не перед кем, достаточно перенести карточку
  const selfAssigned =
    model.selfQuestion ||
    (executors.length === 1 && !!question.author && executors[0].id === question.author.id)

  const showReadiness = !isNew && !selfAssigned && executors.length > 0

  // ответственный, автор задания, ГИП и сотрудник с полным доступом отмечают за исполнителей
  const canManageReadiness = question.canManageReadiness

  const [readinessSaving, setReadinessSaving] = useState(false)

  const onReadiness = useCallback(
    async (ready: boolean, employerId?: string) => {
      setReadinessSaving(true)
      await onSetReadiness(model.id, ready, employerId)
      setReadinessSaving(false)
    },
    [model.id, onSetReadiness]
  )

  const addMessage = useCallback(
    async (message: MessageType) => {
      if (!isNew) {
        return onAddMessage(model.id, message)
      }

      patch({ messages: [message, ...model.messages] })
      return true
    },
    [isNew, model.id, model.messages, onAddMessage, patch]
  )

  const removeMessage = useCallback(
    (message: MessageType, index: number) => {
      if (!isNew && message.id && onDeleteMessage) {
        onDeleteMessage(model.id, message.id)
        return
      }

      patch({ messages: model.messages.filter((_, i) => i !== index) })
    },
    [isNew, model.id, model.messages, onDeleteMessage, patch]
  )

  return (
    <Dialog
      isCloseButtonShown={true}
      isOpen={true}
      onClose={close}
      backdropClassName="standard"
      canOutsideClickClose={false}
      canEscapeKeyClose={false}
      enforceFocus={false}
      className={`figma-dialog ${styles.dialog}`}
      title={
        <div className={styles.dialogTitle}>
          <span className={styles.number}>{isNew ? "Новое задание" : question.number}</span>
          {!isNew && currentColumn && (
            <span className={styles.columnBadge} style={{ backgroundColor: currentColumn.color }}>
              {currentColumn.name}
            </span>
          )}
          {/* ссылкой на задание делятся прямо из карточки: адрес у него постоянный */}
          {!isNew && onShare && (
            <PlanrButton
              type="graybtn"
              icon="general-copy"
              size="small"
              round={true}
              title="Поделиться ссылкой"
              onClick={() => onShare(model.id)}
            />
          )}
        </div>
      }
    >
      <form
        className={styles.form}
        autoComplete="off"
        onSubmit={(e) => {
          e.preventDefault()
          submit()
        }}
      >
      <div className={styles.body}>
        <div className={styles.main}>
          <Section title="Задание">
            <div className={`bp5-form-group ${Classes.SMALL} ${styles.titleField}`}>
              <label className="bp5-label">Заголовок</label>
              <InputGroup
                className={`planr-default-input ${styles.titleInput}`}
                intent={emptyError ? "danger" : "none"}
                value={model.title}
                placeholder="Коротко о задании"
                readOnly={!canEdit}
                autoComplete="off"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => patch({ title: e.target.value })}
              />
            </div>

            <label className="bp5-label">Описание</label>
            <div className={`${Classes.INPUT_GROUP} planr-default-input`}>
              <TextArea
                className={`${Classes.FILL} ${styles.textarea}`}
                intent={emptyError ? "danger" : "none"}
                value={model.description}
                growVertically={true}
                readOnly={!canEdit}
                placeholder="Что нужно сделать"
                onChange={(e) => patch({ description: e.target.value })}
              />
            </div>
            {emptyError && <div className={styles.emptyError}>Заполните заголовок или описание задания</div>}

            {canEdit && (
              <div className={styles.uploader}>
                <PlanRDndUploader
                  accept="*"
                  oneRow={true}
                  noType={true}
                  onFileSelected={async (file: File) => {
                    const uploaded = await upload(file)
                    uploaded && patch({ questionFiles: [...model.questionFiles, uploaded] })
                  }}
                  withLoader={true}
                  multiple={true}
                />
              </div>
            )}

            <div className={styles.files}>
              {model.questionFiles.map((doc, index) => (
                <FileLink
                  baseUrl={baseUrl}
                  file={doc}
                  key={doc.fileId || index}
                  readOnly={!canEdit}
                  onRemove={(file: FileBase) =>
                    patch({ questionFiles: model.questionFiles.filter((f) => f.fileId !== file.fileId) })
                  }
                />
              ))}
            </div>
          </Section>

          {/* исполнителю и ответственному ответ важнее всего — выделяем блок */}
          <Section title="Ответ" accent={waitsAnswer}>
            <div className={`${Classes.INPUT_GROUP} planr-default-input`}>
              <TextArea
                className={`${Classes.FILL} ${styles.textarea}`}
                value={model.answer}
                growVertically={true}
                readOnly={!canAnswer}
                placeholder="Результат работы по заданию"
                onChange={(e) => patch({ answer: e.target.value })}
              />
            </div>

            {canAnswer && (
              <div className={styles.uploader}>
                <PlanRDndUploader
                  accept="*"
                  oneRow={true}
                  noType={true}
                  onFileSelected={async (file: File) => {
                    const uploaded = await upload(file)
                    uploaded && patch({ answerFiles: [...model.answerFiles, uploaded] })
                  }}
                  withLoader={true}
                  multiple={true}
                />
              </div>
            )}

            <div className={styles.files}>
              {model.answerFiles.map((doc, index) => (
                <FileLink
                  baseUrl={baseUrl}
                  file={doc}
                  key={doc.fileId || index}
                  readOnly={!canAnswer}
                  onRemove={(file: FileBase) =>
                    patch({ answerFiles: model.answerFiles.filter((f) => f.fileId !== file.fileId) })
                  }
                />
              ))}
            </div>

            {showReadiness && (
              <div className={styles.readiness}>
                <div className={styles.readinessHead}>
                  {myReadiness
                    ? "Вы отметили, что закончили работу"
                    : iAmExecutor
                    ? "Закончили работу? Отметьте это здесь"
                    : canManageReadiness
                    ? "Готовность исполнителей: можно отметить за любого"
                    : "Готовность исполнителей"}

                  <span className={styles.readinessCount}>
                    {readyCount} / {executors.length}
                  </span>
                </div>

                {executors.map((person) => {
                  const ready = readiness.find((r) => r.employer?.id === person.id)
                  const mine = person.id === currentUserId
                  const editable = mine || canManageReadiness

                  return (
                    <button
                      type="button"
                      key={person.id}
                      className={`${styles.readinessRow} ${editable ? styles.readinessMine : ""}`}
                      disabled={!editable || readinessSaving}
                      title={
                        mine
                          ? ready
                            ? "Снять отметку"
                            : "Отметить, что работа закончена"
                          : editable
                          ? ready
                            ? `Снять отметку за ${person.name}`
                            : `Отметить за ${person.name}, что работа закончена`
                          : undefined
                      }
                      onClick={() => editable && onReadiness(!ready, mine ? undefined : person.id)}
                    >
                      <PlanrButton
                        type="neutral"
                        size="small"
                        icon={ready ? "general-checkbox" : "general-zero-checkbox"}
                      />
                      <span className={styles.readinessName}>{person.name}</span>
                      {ready && <span className={styles.readinessDate}>{ready.day}</span>}
                      {mine ? (
                        <span className={styles.readinessAction}>
                          {ready ? "снять" : "нажмите, если закончили"}
                        </span>
                      ) : (
                        editable && (
                          <span className={styles.readinessAction}>{ready ? "снять" : "отметить за него"}</span>
                        )
                      )}
                    </button>
                  )
                })}

                {showReviewHint && (
                  <div className={styles.reviewHint}>
                    <GeneralIcon type="general-info" size={14} />
                    <span>
                      {iAmLast
                        ? executors.length === 1
                          ? "Как только отметитесь, задание уйдёт на проверку"
                          : "Вы последний — как только отметитесь, задание уйдёт на проверку"
                        : `Дали ответ или выполнили задание — перетащите карточку в колонку «${reviewColumn!.name}»`}
                    </span>
                  </div>
                )}
              </div>
            )}

            {!showReadiness && showReviewHint && (
              <div className={styles.reviewHint}>
                <GeneralIcon type="general-info" size={14} />
                <span>
                  Дали ответ или выполнили задание — перетащите карточку в колонку «{reviewColumn!.name}»
                </span>
              </div>
            )}

            {question.reviewSentBy && (
              <div className={styles.reviewSent}>
                На проверку отправил {question.reviewSentBy.name}
                {question.reviewSentDate ? `, ${question.reviewSentDate}` : ""}
              </div>
            )}
          </Section>

          {!isNew && (question.children.length > 0 || question.canEditQuestion) && (
            <Section
              title="Подзадачи"
              action={
                question.canEditQuestion ? (
                  <PlanrButton type="dashed" size="small" icon="general-plus-small" onClick={() => onCreateChild(question)}>
                    Подзадача
                  </PlanrButton>
                ) : undefined
              }
            >
              <div className={styles.children}>
                {question.children.map((child) => (
                  <div
                    className={styles.child}
                    key={child.id}
                    title={child.title || child.description}
                    onClick={() => onOpenQuestion(child)}
                  >
                    <span className={styles.childNumber}>{child.number}</span>
                    <span className={styles.childText}>{child.title || child.description}</span>
                    <span className={styles.childTerm}>{child.stopDay}</span>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {question.parentQuestion && (
            <Section title="Относится к заданию">
              <div
                className={styles.child}
                title={question.parentQuestion.title || question.parentQuestion.description}
                onClick={() => onOpenQuestion(question.parentQuestion)}
              >
                <span className={styles.childNumber}>{question.parentQuestion.number}</span>
                <span className={styles.childText}>
                  {question.parentQuestion.title || question.parentQuestion.description}
                </span>
                <span className={styles.childTerm}>{question.parentQuestion.stopDay}</span>
              </div>
            </Section>
          )}

        </div>

        <div className={styles.sidebar}>
          <div className={styles.sectionHead}>
            <h3>Настройки</h3>
          </div>

          <Field label="Колонка">
            <SimpleSelect
              className={`full-width-select ${Classes.FILL}`}
              activeItem={columnItem}
              items={columnItems}
              itemRenderer={renderSingleOption}
              filterable={false}
              disabled={!isNew && !question.canMoveQuestion}
              onItemSelect={(item: SelectItem) =>
                patch({ columnId: item.id, completed: item.id === SystemColumns.done })
              }
              popoverProps={{ rootBoundary: "viewport", minimal: true }}
            >
              <DefaultSelectedOption option={columnItem} what="Колонка" />
            </SimpleSelect>
          </Field>

          {isNew && canAddQuestion && (
            <Field label="Кому">
              <div className={styles.switcher}>
                <PlanrButton
                  type={!model.selfQuestion ? "primary" : "graybtn"}
                  size="small"
                  onClick={() => patch({ selfQuestion: false })}
                >
                  Сотруднику
                </PlanrButton>
                <PlanrButton
                  type={model.selfQuestion ? "primary" : "graybtn"}
                  size="small"
                  onClick={() => patch({ selfQuestion: true, employerIds: [], implementerIds: [] })}
                >
                  Себе
                </PlanrButton>
              </div>
            </Field>
          )}

          {orders && !hideProject && (
            <Field label="Проект">
              <ProjectSelect
                className="full-width-select"
                filterable={true}
                itemPredicate={filterProjectItemPredicate}
                activeItem={project}
                items={orders.orders}
                itemRenderer={renderProjectOption}
                disabled={!canEdit}
                onItemSelect={(item: OrderDictionaryItemType | null) =>
                  patch({
                    orderId: item ? item.id : "",
                    // ГИП проекта подставляется ответственным, только если он вообще может
                    // получить задание на этой доске — иначе бэк его отбросит и поле опустеет
                    employerIds:
                      item &&
                      item.employer &&
                      model.employerIds.length === 0 &&
                      people.some((person) => person.id === item.employer!.id)
                        ? [item.employer.id]
                        : model.employerIds,
                  })
                }
                resetOnClose={true}
                inputProps={{ placeholder: "Проект" }}
              >
                <ProjectSelectedOption option={project} onClear={() => patch({ orderId: "" })} />
              </ProjectSelect>
            </Field>
          )}

          <Field label="Срок исполнения">
            <div className={expired ? styles.expiredDate : undefined}>
              <DatePicker
                rootBoundary="viewport"
                value={stopDate ? stopDate.toDate() : null}
                clasName="planr-default-input"
                // срок задания меняет тот, кто его выдал, а не исполнители
                disabled={!canEdit}
                onChange={(date) => patch({ stopDate: date ? moment(date).format(DATE_FORMAT) : "" })}
              />
            </div>
            {question.oldStopDates && question.oldStopDates.length > 0 && (
              <div className={styles.oldDates}>Прежние сроки: {question.oldStopDates.join(", ")}</div>
            )}
          </Field>

          <Field label="Теги">
            <TagsSelect
              known={tags}
              selected={model.tags}
              colors={model.tagColors}
              disabled={!canEdit}
              onChange={(value) => patch({ tags: value })}
              onColor={(tag, color) => patch({ tagColors: { ...model.tagColors, [tag]: color } })}
            />
          </Field>

          <Field label="Ответственные">
            <People
              people={people}
              selected={model.employerIds}
              assigned={question.employers}
              disabled={!canAssign || model.selfQuestion}
              onChange={(ids) => patch({ employerIds: ids })}
            />
          </Field>

          <Field label="Исполнители">
            <People
              people={people}
              selected={model.implementerIds}
              assigned={question.implementers}
              disabled={!canAssign || model.selfQuestion}
              onChange={(ids) => patch({ implementerIds: ids })}
            />
          </Field>


          {!isNew && (
            <div className={styles.info}>
              <div>
                <GeneralIcon type="general-contact" size={12} />
                Инициатор: {question.author?.name || "—"}
              </div>
              <div>
                <GeneralIcon type="general-calendar" size={12} />
                Создано: {question.day}
              </div>
            </div>
          )}
        </div>

        <div className={styles.commentsColumn}>
          <div className={styles.sectionHead}>
            <h3>Комментарии{model.messages.length > 0 ? ` · ${model.messages.length}` : ""}</h3>
          </div>

          <QuestionComments
            messages={model.messages}
            currentUserId={currentUserId}
            onAdd={addMessage}
            onEdit={(message) => (isNew ? Promise.resolve(true) : onAddMessage(model.id, message))}
            onRemove={removeMessage}
          />
        </div>
      </div>

      {/* кнопку удаления прячем без прав так же, как у нового задания */}
      <div className={Classes.DIALOG_FOOTER}>
        <RemoveConfirmation<SaveQuestion>
          what={() => `задание ${model.title || model.id}`}
          onConfirmed={(value) => value && onRemove(value)}
        >
          {({ confirmRemoving }) => (
            <StandardFormButtons
              {...({ dirty, isSubmitting: saving, resetForm: close, submitForm: submit } as any)}
              isRemoved={false}
              isNewlyCreated={isNew || !question.canRemoveQuestion}
              what="задание"
              onRemove={() => confirmRemoving(model)}
              submitLabel={texts.save}
            />
          )}
        </RemoveConfirmation>
      </div>
      </form>
    </Dialog>
  )
}

const Section: React.FC<{ title: string; action?: React.ReactNode; accent?: boolean; children?: React.ReactNode }> = ({
  title,
  action,
  accent,
  children,
}) => (
  <div className={`${styles.section} ${accent ? styles.accent : ""}`}>
    <div className={styles.sectionHead}>
      <h3>{title}</h3>
      {action}
    </div>
    {children}
  </div>
)

const Field: React.FC<{ label: string; children?: React.ReactNode }> = ({ label, children }) => (
  <div className={`bp5-form-group ${Classes.SMALL} ${styles.field}`}>
    <label className="bp5-label">{label}</label>
    {children}
  </div>
)

/**
 * Метки задания: выбираются из справочника открытой доски, недостающая заводится прямо
 * здесь — дальше она предлагается в остальных заданиях этой же доски
 */
const TagsSelect: React.FC<TagsSelectProps> = ({ known, selected, colors, disabled, onChange, onColor }) => {
  // только что заведённый тег в справочник ещё не попал — показываем его в списке сразу
  const names = known.map((tag) => tag.name)
  const items = names
    .concat(selected.filter((tag) => !names.includes(tag)))
    .map((name) => ({ id: name, label: name }))

  const colorOf = (tag: string) =>
    colors[tag] || known.find((t) => t.name === tag)?.color || DEFAULT_TAG_COLOR

  const toggle = (tag: string) =>
    onChange(selected.includes(tag) ? selected.filter((t) => t !== tag) : [...selected, tag])

  return (
    <>
      <TagMultiSelect
        className="full-width-select"
        fill={true}
        itemRenderer={renderCheckedOption(selected, colorOf)}
        itemsEqual={(a: SelectItem, b: SelectItem) => a.id === b.id}
        selectedItems={selected.map((tag) => ({ id: tag, label: tag }))}
        items={items}
        onItemSelect={(item: SelectItem) => toggle(item.id)}
        placeholder="Выберите тег или введите новый"
        itemPredicate={filterItemPredicate}
        tagRenderer={(item: SelectItem) => item.label}
        noResults={<MenuItem disabled={true} text="Начните вводить название" />}
        popoverProps={MATCH_TARGET_WIDTH}
        // список остаётся открытым, поэтому активный пункт не сбрасываем:
        // иначе Blueprint возвращает его на первый и прокручивает список к началу
        resetOnSelect={false}
        createNewItemFromQuery={(query: string) => ({ id: query.trim(), label: query.trim() })}
        createNewItemRenderer={(query: string, active: boolean, handleClick: any) => (
          <MenuItem
            icon="add"
            text={`Создать тег «${query.trim()}»`}
            active={active}
            onClick={handleClick}
            shouldDismissPopover={false}
          />
        )}
        tagInputProps={{
          disabled,
          // подсказка в поле остаётся видной и когда теги уже выбраны
          inputProps: { placeholder: "Введите название нового тега" },
          onRemove: (_value: any, index: number) => onChange(selected.filter((_, i) => i !== index)),
        }}
      />

      {selected.length > 0 && (
        <div className={styles.tagColors}>
          {selected.map((tag) => (
            <Popover
              key={tag}
              disabled={disabled}
              position="bottom-left"
              rootBoundary="viewport"
              content={
                <div className={styles.palette}>
                  {[DEFAULT_TAG_COLOR, ...COLUMN_COLORS].map((color) => (
                    <button
                      type="button"
                      key={color}
                      className={`${styles.paletteColor} ${colorOf(tag) === color ? styles.paletteActive : ""}`}
                      style={{ backgroundColor: color }}
                      onClick={() => onColor(tag, color)}
                    />
                  ))}
                </div>
              }
            >
              <button
                type="button"
                className={styles.tagChip}
                style={{ color: colorOf(tag) }}
                title="Выбрать цвет тега"
              >
                <span className={styles.tagDot} style={{ backgroundColor: colorOf(tag) }} />
                {tag}
              </button>
            </Popover>
          ))}
        </div>
      )}
    </>
  )
}

interface TagsSelectProps {
  known: QuestionTagSnapshotType[]
  selected: string[]
  colors: TStringMap<string>
  disabled?: boolean
  onChange: (tags: string[]) => void
  onColor: (tag: string, color: string) => void
}

const People: React.FC<PeopleProps> = ({ people, selected, assigned, disabled, onChange }) => {
  // уволенных в справочнике нет, но в задании они назначены — берём имя из самого задания
  const items = selected.map((id) => {
    const known = people.find((e) => e.id === id)

    if (known) {
      return { id: known.id, label: known.label }
    }

    const person = (assigned || []).find((e) => e.id === id)

    return { id, label: person ? person.name : "Сотрудник удалён" }
  })

  return (
    <PeopleSelect
      className="full-width-select"
      fill={true}
      itemRenderer={renderCheckedOption(selected)}
      itemsEqual={(a: SelectItem, b: SelectItem) => a.id === b.id}
      selectedItems={items}
      items={people}
      onItemSelect={(item: SelectItem) =>
        onChange(selected.includes(item.id) ? selected.filter((id) => id !== item.id) : [...selected, item.id])
      }
      placeholder="Не назначены"
      itemPredicate={filterItemPredicate}
      tagRenderer={(item: SelectItem) => item.label}
      noResults={<MenuItem disabled={true} text={texts.noData} />}
      popoverProps={MATCH_TARGET_WIDTH}
      // после выбора строку поиска сбрасываем: следующего ищут с чистого листа
      resetOnSelect={true}
      tagInputProps={{
        disabled,
        onRemove: (_value: any, index: number) => onChange(selected.filter((_, i) => i !== index)),
      }}
    />
  )
}

interface PeopleProps {
  people: SelectItem[]
  selected: string[]
  /** Кто уже назначен на задание: у уволенных имя есть только здесь */
  assigned?: { id: string; name: string }[]
  disabled?: boolean
  onChange: (ids: string[]) => void
}

interface QuestionDialogProps {
  value: SaveQuestion
  question: OrderQuestionSnapshotType
  columns: BoardColumnSnapshotType[]
  /** Справочник меток для подсказок */
  tags: QuestionTagSnapshotType[]
  employee: EmployerDictionaryType
  orders?: OrderDictionaryType
  baseUrl: string
  hideProject?: boolean
  currentUserId: string
  canAddQuestion?: boolean
  onClose: () => void
  /** Копирование ссылки на задание в буфер: адрес у задания постоянный */
  onShare?: (id: string) => void
  /** Участники личной доски: владелец и приглашённые. Пусто — доска по проектам */
  portfolioMembers?: { id: string; name: string }[] | null
  onSetReadiness: (questionId: string, ready: boolean, employerId?: string) => Promise<any>
  onSubmit: (value: SaveQuestion) => Promise<boolean>
  onRemove: (value: SaveQuestion) => void
  onAddMessage: (questionId: string, message: MessageType) => Promise<any>
  onDeleteMessage?: (questionId: string, messageId: string) => Promise<any>
  onCreateChild: (parent: OrderQuestionSnapshotType) => void
  onOpenQuestion: (question: any) => void
  upload: (file: File) => Promise<FileBase | null>
}
